/**
 * Talks to the Department of Social Services allowance API (bhata.gov.bd).
 *
 * Browser-direct on purpose: this tool has no backend of its own. The API
 * answers CORS preflights by reflecting whatever Origin asks, so the fetch below
 * works from the app and from a dev server alike — nothing here goes through
 * sohozkaj's own API, and no tracking id is ever stored on our side.
 *
 * Two calls make up one lookup:
 *
 *   GET  /api/v1/captcha              → { captcha_token, captcha_image }
 *   POST /api/v1/global/applicants_tracking
 *        multipart form: tracking_type, tracking_no | (nid + date_of_birth),
 *        captcha_token, captcha_value
 *
 * The captcha token is single-use — a lookup consumes it whether it succeeded or
 * not, so every caller refreshes the picture afterwards.
 */

import { toBnDigits } from '../../../../../utils/bnFormat';

const API_BASE = 'https://api.bhata.gov.bd/api/v1';

/** Tracking id lookup vs NID + date-of-birth lookup, as the upstream API numbers them. */
export const TRACKING_TYPE = {
    byTrackingNo: 2,
    byNid: 1,
};

/** The four committee stages, in the order the API returns them. */
const COMMITTEE_STEPS = [
    {
        key: 'verification',
        name_bn: 'প্রাথমিকভাবে যাচাইকরণ',
        name_en: 'Primarily Verification',
    },
    {
        key: 'ward',
        name_bn: 'ওয়ার্ড/ইউনিয়ন/পৌরসভা কমিটি',
        name_en: 'Ward/Union/Pouroshava Committee',
    },
    {
        key: 'district',
        name_bn: 'জেলা পৌরসভা/উপজেলা/সিটি কর্পোরেশন কমিটি',
        name_en: 'District Pourashava/Upazila/City Corporation Committee',
    },
    {
        key: 'final',
        name_bn: 'চূড়ান্তভাবে নির্বাচিত',
        name_en: 'Final Selection List',
    },
];

/**
 * Upstream status codes, collapsed into the two states the official tracker
 * actually names in its legend: সম্পন্ন (it paints 1/2/5 green) and প্রক্রিয়াধীন
 * (3 and 4). Anything else gets NO label — this tool reports what the API says
 * and refuses to invent a meaning for a code the department has not published.
 */
export function stepStateFromStatus(status) {
    switch (Number(status)) {
        case 1:
        case 2:
        case 5:
            return 'done';
        case 3:
        case 4:
            return 'running';
        default:
            return 'unknown';
    }
}

/** Bangla digits in, ASCII digits out — the API only accepts the latter. */
export function toAsciiDigits(value) {
    return String(value ?? '')
        .replace(/[০-৯]/g, (digit) => String.fromCharCode(digit.charCodeAt(0) - 0x09e6 + 48))
        .replace(/[^\d]/g, '');
}

function apiUrl(path, lang) {
    return `${API_BASE}/${path}?lang=${lang === 'en' ? 'en' : 'bn'}`;
}

/** The first useful line out of a Laravel validation payload. */
function messageFromPayload(payload, fallback) {
    if (!payload || typeof payload !== 'object') return fallback;
    const firstFieldError = Object.values(payload.errors || {})
        .flat()
        .find((entry) => typeof entry === 'string' && entry.trim());
    return firstFieldError || payload.message || fallback;
}

class BhataTrackingError extends Error {
    constructor(message, { code = 'REQUEST_FAILED', status = 0 } = {}) {
        super(message);
        this.name = 'BhataTrackingError';
        this.code = code;
        this.status = status;
    }
}

/** A fresh captcha picture plus the token that answers for it. */
export async function fetchCaptcha(lang = 'bn') {
    let response;
    try {
        response = await fetch(apiUrl('captcha', lang), {
            method: 'GET',
            headers: { Accept: 'application/json' },
        });
    } catch {
        throw new BhataTrackingError('NETWORK', { code: 'NETWORK' });
    }

    if (!response.ok) {
        throw new BhataTrackingError('CAPTCHA_LOAD', { code: 'CAPTCHA_LOAD', status: response.status });
    }

    const payload = await response.json().catch(() => null);
    if (!payload?.captcha_token || !payload?.captcha_image) {
        throw new BhataTrackingError('CAPTCHA_LOAD', { code: 'CAPTCHA_LOAD', status: response.status });
    }

    return { token: payload.captcha_token, image: payload.captcha_image };
}

/**
 * One tracking lookup.
 *
 * `Accept: application/json` is not optional — without it the API answers a
 * browser-shaped 302 redirect instead of the JSON error, and the caller sees an
 * opaque failure rather than "wrong captcha".
 *
 * The body goes as multipart/form-data (what the official site sends); letting
 * fetch set the boundary itself is why no Content-Type is passed here.
 */
export async function fetchTracking({
    trackingType = TRACKING_TYPE.byTrackingNo,
    trackingNo = '',
    nid = '',
    dateOfBirth = '',
    captchaToken,
    captchaValue,
    lang = 'bn',
}) {
    const form = new FormData();
    form.append('tracking_type', String(trackingType));
    if (Number(trackingType) === TRACKING_TYPE.byTrackingNo) {
        form.append('tracking_no', toAsciiDigits(trackingNo));
    } else {
        form.append('nid', toAsciiDigits(nid));
        form.append('date_of_birth', dateOfBirth);
    }
    form.append('captcha_token', captchaToken || '');
    form.append('captcha_value', captchaValue || '');

    let response;
    try {
        response = await fetch(apiUrl('global/applicants_tracking', lang), {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: form,
        });
    } catch {
        throw new BhataTrackingError('NETWORK', { code: 'NETWORK' });
    }

    const payload = await response.json().catch(() => null);

    if (!response.ok) {
        const invalidCaptcha = Boolean(payload?.errors?.captcha_value);
        throw new BhataTrackingError(
            messageFromPayload(payload, ''),
            {
                code: invalidCaptcha ? 'CAPTCHA_FAILED' : 'REQUEST_FAILED',
                status: response.status,
            },
        );
    }

    const details = payload?.data || null;
    // The API answers 200 with an empty payload for an id it does not know.
    if (!details || !(details.id || details.application_id || details.verification_number)) {
        throw new BhataTrackingError('', { code: 'NOT_FOUND', status: response.status });
    }

    return details;
}

function pickName(entry, lang) {
    if (!entry) return '';
    return (lang === 'en' ? entry.name_en : entry.name_bn) || entry.name_en || entry.name_bn || '';
}

/**
 * The API payload, reshaped into what the preview and the print sheet draw.
 *
 * Timeline mirrors the official site: an "application accepted" step built from
 * `created_at`, then ONE STEP PER COMMITTEE ENTRY THE RESPONSE ACTUALLY
 * CONTAINS — never more. The entries carry only a status code and a timestamp,
 * so the stage names below are matched positionally, exactly as the official
 * tracker at dss.bhata.gov.bd does with the same payload.
 */
export function normalizeTracking(details, lang = 'bn') {
    const committee = Array.isArray(details?.committee_application) ? details.committee_application : [];
    const created = details?.created_at ? new Date(details.created_at) : null;
    const locale = lang === 'en' ? 'en-GB' : 'bn-BD';

    const formatDate = (date) => (date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString(locale) : '');
    const formatTime = (date) => (date && !Number.isNaN(date.getTime()) ? date.toLocaleTimeString(locale) : '');

    // Numbers are shown in Bangla numerals on a Bangla page, the way the
    // official tracker prints them.
    const localizeDigits = (value) => (lang === 'bn' ? toBnDigits(value || '') : String(value || ''));

    const steps = [{
        key: 'accepted',
        // Not a committee entry — this is `created_at` itself, i.e. the moment
        // the department recorded the application.
        title: lang === 'en' ? 'Your application has been accepted' : 'আপনার আবেদন গৃহীত হয়েছে',
        stepState: 'done',
        date: formatDate(created),
        time: formatTime(created),
    }];

    committee.forEach((entry, index) => {
        const step = COMMITTEE_STEPS[index];
        if (!step) return;
        const at = entry?.created_at ? new Date(entry.created_at) : null;
        steps.push({
            key: step.key,
            title: lang === 'en' ? step.name_en : step.name_bn,
            stepState: stepStateFromStatus(entry?.status),
            date: formatDate(at),
            time: formatTime(at),
        });
    });

    // Stages the response does NOT contain are not shown at all. Padding the
    // timeline with greyed-out "upcoming" rows would be this tool asserting
    // something about an application that the department never said.

    const office = details?.office_has_ward?.office || details?.office || null;

    return {
        applicationId: localizeDigits(details?.application_id),
        verificationNumber: localizeDigits(details?.verification_number),
        programName: pickName(details?.program, lang),
        officeName: pickName(office, lang),
        applicationDate: formatDate(created),
        applicationTime: formatTime(created),
        steps,
        latestStep: steps[steps.length - 1],
        raw: details,
    };
}
